// src/hooks/useImageAspectRatioGuard.ts
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../components/Toast";
import {
  readImageFileDimensions,
  validateImageAspectRatio,
  aspectRatioToastParams,
  type ImageDimensions,
} from "../utils/imageDimensions";

export type ImageAspectRatioGuard = (file: File) => Promise<boolean>;

/**
 * 共享的图片宽高比校验闸：读取图片尺寸 → 校验宽高比（默认 0.5~2）。
 * 不通过时 toast 提示并返回 false；通过返回 true。
 * 用于各「点击选择 / 拖拽 / 粘贴」上传位的统一前置校验，调用方在 true 时才继续上传。
 */
export function useImageAspectRatioGuard(): ImageAspectRatioGuard {
  const { t } = useTranslation();
  const toast = useToast();

  return useCallback(
    async (file: File): Promise<boolean> => {
      let dimensions: ImageDimensions;
      try {
        dimensions = await readImageFileDimensions(file);
      } catch {
        toast.error(t("imageReadFailed", { ns: "common" }));
        return false;
      }
      const error = validateImageAspectRatio(dimensions);
      if (error) {
        toast.error(t("imageAspectRatioInvalid", aspectRatioToastParams(error)));
        return false;
      }
      return true;
    },
    [t, toast],
  );
}
